import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { resolveRichText } from '@vibesites/runtime';
import { useEditMode, useSlotGroup, useVibesites } from '@vibesites/runtime/react';
import { EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser } from '@fortawesome/free-solid-svg-icons/faUser';
import { useStaggerReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { useColor4 } from '../hooks/useColor4';

/**
 * Stacked team member list for the Meet The Team page — three on-site team
 * member cards (photo beside text from sm: up), each rendered from a single slot group so operators edit the whole
 * card (photo, name, title, bio) in one panel.
 *
 * A card with no photo, name, title, or bio does not render on the live site
 * (and the whole section is skipped when every card is empty). In edit mode
 * empty cards still render with a notice so operators can reach the editor
 * to fill them in.
 *
 * Each card's slot group definition is a literal module-level const so the
 * schema linter can statically extract the schema.
 */

// ============================================================================
// Static slot group definitions (literal objects for contentSchema extraction)
// ============================================================================
//
// NOTE: these groups intentionally have no `sourceBinding`. The sibling
// text fields (name, title, bio) are independent of the photo —
// swapping the image via the media picker must not blow away a hand-written
// bio. The `slot-group-missing-source-binding` warning is expected here
// (same shape as RewardsPrograms' cards).

const member1Def: SlotGroupDefinition = {
  label: 'Team Member 1',
  fields: {
    photo: {
      label: 'Photo',
      type: 'image',
      defaultValue: '',
    },
    photo_alt: {
      label: 'Photo Alt Text',
      type: 'text',
      defaultValue: '',
      generator: {
        prompt:
          'Alt text for the photo currently set on this card: a short, literal description of what the image shows (the person, their setting, what they are doing). Include the team member\'s name and role when known. Do not start with "Image of" or "Photo of". Leave empty if the card has no photo.',
        context: 'Portrait beside a team member\'s name, title, and bio on the Meet The Team page.',
        length: 'under 125 characters',
      },
    },
    name: {
      label: 'Name',
      type: 'text',
      defaultValue: '',
    },
    title: {
      label: 'Title',
      type: 'text',
      defaultValue: '',
    },
    bio: {
      label: 'Bio',
      type: 'richtext',
      defaultValue: '',
    },
  },
  generator: {
    prompt:
      'A real on-site Property Manager at this property — first name + last initial (e.g. "Mohammed M."), a warm 1-2 sentence bio that may reference the property by name or mention a hobby, and a friendly professional portrait if one exists in the gallery (otherwise leave the photo empty so the blank placeholder shows). Whenever the card has a photo — picked here or already set by an editor — write `photo_alt` describing what that photo actually shows; leave `photo_alt` empty when there is no photo. Leave `title` alone.',
    context:
      'Card 1 of 3 on the Meet The Team page. Bio and photo must feel distinct from cards 2 (Leasing Professional) and 3 (Maintenance Supervisor). If this property has no Property Manager on record, leave the name, bio, and photo empty so the card stays hidden on the live site.',
  },
};

const member2Def: SlotGroupDefinition = {
  label: 'Team Member 2',
  fields: {
    photo: {
      label: 'Photo',
      type: 'image',
      defaultValue: '',
    },
    photo_alt: {
      label: 'Photo Alt Text',
      type: 'text',
      defaultValue: '',
      generator: {
        prompt:
          'Alt text for the photo currently set on this card: a short, literal description of what the image shows (the person, their setting, what they are doing). Include the team member\'s name and role when known. Do not start with "Image of" or "Photo of". Leave empty if the card has no photo.',
        context: 'Portrait beside a team member\'s name, title, and bio on the Meet The Team page.',
        length: 'under 125 characters',
      },
    },
    name: {
      label: 'Name',
      type: 'text',
      defaultValue: '',
    },
    title: {
      label: 'Title',
      type: 'text',
      defaultValue: '',
    },
    bio: {
      label: 'Bio',
      type: 'richtext',
      defaultValue: '',
    },
  },
  generator: {
    prompt:
      'A real on-site Leasing Professional at this property — first name + last initial (e.g. "Justiana M."), a warm 1-2 sentence bio that may mention a hobby or interest, and a friendly professional portrait if one exists in the gallery (otherwise leave the photo empty so the blank placeholder shows). Whenever the card has a photo — picked here or already set by an editor — write `photo_alt` describing what that photo actually shows; leave `photo_alt` empty when there is no photo. Leave `title` alone.',
    context:
      'Card 2 of 3 on the Meet The Team page. Bio and photo must feel distinct from cards 1 (Property Manager) and 3 (Maintenance Supervisor). If this property has no Leasing Professional on record, leave the name, bio, and photo empty so the card stays hidden on the live site.',
  },
};

const member3Def: SlotGroupDefinition = {
  label: 'Team Member 3',
  fields: {
    photo: {
      label: 'Photo',
      type: 'image',
      defaultValue: '',
    },
    photo_alt: {
      label: 'Photo Alt Text',
      type: 'text',
      defaultValue: '',
      generator: {
        prompt:
          'Alt text for the photo currently set on this card: a short, literal description of what the image shows (the person, their setting, what they are doing). Include the team member\'s name and role when known. Do not start with "Image of" or "Photo of". Leave empty if the card has no photo.',
        context: 'Portrait beside a team member\'s name, title, and bio on the Meet The Team page.',
        length: 'under 125 characters',
      },
    },
    name: {
      label: 'Name',
      type: 'text',
      defaultValue: '',
    },
    title: {
      label: 'Title',
      type: 'text',
      defaultValue: '',
    },
    bio: {
      label: 'Bio',
      type: 'richtext',
      defaultValue: '',
    },
  },
  generator: {
    prompt:
      'A real on-site Maintenance Supervisor at this property — first name + last initial (e.g. "Aws H."), a warm 1-2 sentence bio that may mention a hobby or interest, and a friendly professional portrait if one exists in the gallery (otherwise leave the photo empty so the blank placeholder shows). Whenever the card has a photo — picked here or already set by an editor — write `photo_alt` describing what that photo actually shows; leave `photo_alt` empty when there is no photo. Leave `title` alone.',
    context:
      'Card 3 of 3 on the Meet The Team page. Bio and photo must feel distinct from cards 1 (Property Manager) and 2 (Leasing Professional). If this property has no Maintenance Supervisor on record, leave the name, bio, and photo empty so the card stays hidden on the live site.',
  },
};

// ============================================================================
// Helpers
// ============================================================================

/**
 * Extract monogram initials from a name. Returns an em-dash fallback so the
 * empty-photo / empty-name case still renders a visible glyph.
 */
function getInitials(name: string | null | undefined): string {
  if (!name) return '—';
  const text = name
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .trim();
  if (!text) return '—';
  const letters = text
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join('');
  return letters || '—';
}

function hasText(value: string | null | undefined): boolean {
  if (!value) return false;
  return (
    String(value)
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .trim() !== ''
  );
}

function hasMemberContent(member: Record<string, string | null | undefined>): boolean {
  return (
    hasText(member.photo) ||
    hasText(member.name) ||
    hasText(member.title) ||
    hasText(member.bio)
  );
}

function HiddenCardNotice() {
  return (
    <div className="team-hidden-notice">
      <p>Hidden on the live site until a photo, name, title, or bio is added.</p>
    </div>
  );
}

// ============================================================================
// Component
// ============================================================================

interface MemberCardProps {
  groupPath: string;
  definition: SlotGroupDefinition;
}

function TeamMemberCard({ groupPath, definition }: MemberCardProps) {
  const member = useSlotGroup(groupPath, definition);
  const { property } = useVibesites();
  const isEditMode = useEditMode()?.isEditMode ?? false;

  const visible = hasMemberContent(member);
  if (!visible && !isEditMode) return null;

  const bioHtml = resolveRichText(member.bio || '', property as Record<string, any>);
  const hasName = !!member.name?.trim();

  return (
    <EditableSlotGroup
      groupPath={groupPath}
      definition={definition}
      className="team-card-cell"
    >
      <article
        data-reveal
        className={`reveal-fade-up team-card relative bg-white border border-[var(--vibe-color-border)] flex flex-col sm:flex-row overflow-hidden${!visible ? ' team-card--hidden' : ''}`}
      >
        {!visible && <HiddenCardNotice />}
        <div className="relative block w-full sm:w-56 md:w-64 lg:w-72 shrink-0 aspect-[4/5] overflow-hidden bg-[var(--vibe-color-surface)]">
          {member.photo ? (
            <VibesitesImage
              url={member.photo}
              alt={member.photo_alt || `${member.name || 'Team member'} portrait`}
              sizes="(min-width: 1024px) 288px, (min-width: 768px) 256px, (min-width: 640px) 224px, 100vw"
              className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-700 ease-out hover:scale-[1.03]"
            />
          ) : (
            <div className="team-monogram absolute inset-0 flex items-center justify-center" aria-hidden="true">
              {hasName ? (
                <span>{getInitials(member.name)}</span>
              ) : (
                <FontAwesomeIcon icon={faUser} className="team-placeholder-icon" />
              )}
            </div>
          )}
        </div>
        <div className="flex flex-col justify-center text-center sm:text-left p-7 sm:p-8 md:p-10 flex-1 min-w-0">
          {hasName ? (
            <h3 className="font-heading text-xl sm:text-2xl font-normal text-[var(--vibe-color-text)] leading-tight tracking-tight mb-1">
              {member.name}
            </h3>
          ) : (
            <div className="team-placeholder-line h-6 w-40 mb-2 mx-auto sm:mx-0" aria-hidden="true" />
          )}
          {member.title ? (
            <p className="font-body text-[11px] sm:text-xs uppercase tracking-[0.25em] text-[var(--vibe-color-primary)] mb-4">
              {member.title}
            </p>
          ) : null}
          {bioHtml ? (
            <div
              className="font-body text-sm sm:text-base leading-relaxed text-[var(--vibe-color-text-muted)] [&_p]:mb-2 last:[&_p]:mb-0"
              data-vibesite-richtext=""
              dangerouslySetInnerHTML={{ __html: bioHtml }}
            />
          ) : (
            <div className="flex flex-col gap-2 items-center sm:items-start" aria-hidden="true">
              <div className="team-placeholder-line h-3 w-full max-w-md" />
              <div className="team-placeholder-line h-3 w-4/5 max-w-sm" />
            </div>
          )}
        </div>
      </article>
    </EditableSlotGroup>
  );
}

export function TeamMembers() {
  useRevealStyles();
  const gridRef = useStaggerReveal<HTMLDivElement>({ staggerMs: 140 });
  const color4 = useColor4();
  const isEditMode = useEditMode()?.isEditMode ?? false;
  const member1 = useSlotGroup('teammembers.member1', member1Def);
  const member2 = useSlotGroup('teammembers.member2', member2Def);
  const member3 = useSlotGroup('teammembers.member3', member3Def);

  if (
    !isEditMode &&
    !hasMemberContent(member1) &&
    !hasMemberContent(member2) &&
    !hasMemberContent(member3)
  ) {
    return null;
  }

  return (
    <section
      className="w-full py-12 md:py-20 bg-white"
      style={color4?.primaryOverride}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={gridRef} className="flex flex-col gap-8 md:gap-10">
          <TeamMemberCard groupPath="teammembers.member1" definition={member1Def} />
          <TeamMemberCard groupPath="teammembers.member2" definition={member2Def} />
          <TeamMemberCard groupPath="teammembers.member3" definition={member3Def} />
        </div>

        {/* Schema-lint anchors — hidden EditableSlotGroups with literal
            groupPath props so the static schema extractor can match each
            card's slot group. The real interactive wrappers live inside
            <TeamMemberCard> but use dynamic `groupPath` props the extractor
            can't follow. Same pattern as Aster's MeetTheTeamMembers. */}
        <EditableSlotGroup
          groupPath="teammembers.member1"
          definition={member1Def}
          className="hidden"
          style={{ display: 'none' }}
        >
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup
          groupPath="teammembers.member2"
          definition={member2Def}
          className="hidden"
          style={{ display: 'none' }}
        >
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup
          groupPath="teammembers.member3"
          definition={member3Def}
          className="hidden"
          style={{ display: 'none' }}
        >
          <span />
        </EditableSlotGroup>
      </div>

      <style>{`
        .team-card-cell {
          display: flex;
          flex-direction: column;
        }
        .team-card-cell > * {
          flex: 1 1 auto;
        }
        .team-card {
          border-radius: 4px;
          box-shadow: 0 4px 20px -12px rgba(0, 0, 0, 0.25);
          transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
        }
        .team-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 20px 36px -16px rgba(0, 0, 0, 0.28);
          border-color: color-mix(in srgb, var(--vibe-color-primary) 50%, var(--vibe-color-border));
        }
        .team-card--hidden {
          opacity: 0.55;
          outline: 2px dashed color-mix(in srgb, var(--vibe-color-primary) 60%, var(--vibe-color-border));
          outline-offset: -2px;
        }
        .team-hidden-notice {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          z-index: 2;
          background: #fef3c7;
          color: #78350f;
          padding: 0.6rem 1rem;
          font-family: var(--vibe-font-body);
          font-size: 0.75rem;
          font-weight: var(--vibe-font-weight-semibold);
          text-align: center;
          border-bottom: 1px solid rgba(0, 0, 0, 0.05);
        }
        .team-hidden-notice p { margin: 0; }
        .team-hidden-notice code {
          background: rgba(0, 0, 0, 0.08);
          padding: 0 0.3em;
          border-radius: 2px;
          font-size: 0.9em;
        }
        .team-monogram {
          background: linear-gradient(
            135deg,
            color-mix(in srgb, var(--vibe-color-primary) 14%, #ffffff) 0%,
            color-mix(in srgb, var(--vibe-color-primary) 22%, #ffffff) 100%
          );
        }
        .team-monogram span {
          font-family: var(--vibe-font-heading);
          font-weight: var(--vibe-font-weight-normal);
          font-size: clamp(3rem, 7vw, 4.5rem);
          letter-spacing: 0.08em;
          color: var(--vibe-color-primary);
          line-height: 1;
        }
        .team-placeholder-icon {
          width: 35%;
          height: 35%;
          color: color-mix(in srgb, var(--vibe-color-primary) 35%, #ffffff);
        }
        .team-placeholder-line {
          border-radius: 2px;
          background: color-mix(in srgb, var(--vibe-color-text) 8%, #ffffff);
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'TeamMembers',
  displayName: 'Team Members',
  tokens: {},
  slotGroups: {
    'teammembers.member1': member1Def,
    'teammembers.member2': member2Def,
    'teammembers.member3': member3Def,
  },
};

(TeamMembers as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default TeamMembers;
